import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import LegalFooter from "../components/LegalFooter";
import { LEGAL } from "../lib/legal";

/**
 * Static Terms of Service page. Plain prose, kept under a single
 * scrollable container so it prints cleanly. Uses brand details from
 * lib/legal.js so a rebrand only needs to update one file.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-gray-900" data-testid="terms-page">
      <header className="border-b border-gray-300 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" />
          <Link
            to="/login"
            data-testid="terms-back-link"
            className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-10 w-full">
        <div className="text-[10px] uppercase tracking-widest text-gray-500">Legal</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mt-1 mb-2">
          Terms of Service
        </h1>
        <p className="text-xs text-gray-500 mb-8">
          Effective {LEGAL.effectiveDate} · Last updated {LEGAL.effectiveDate}
        </p>

        <Section title="1. Acceptance">
          By accessing or using <strong>{LEGAL.productName}</strong> (the &ldquo;Service&rdquo;)
          you agree to be bound by these Terms of Service (the &ldquo;Terms&rdquo;). If you
          are using the Service on behalf of {LEGAL.company} (the &ldquo;Company&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;), you confirm you are authorised by the Company to do so and
          to bind the Company to these Terms.
        </Section>

        <Section title="2. Eligibility & Accounts">
          You must be at least 16 years old and authorised by the Company to
          access the Service. Each account is personal — you must not share
          your password, security questions, or session with anyone else.
          You are responsible for everything that happens under your account.
        </Section>

        <Section title="3. Acceptable Use">
          You agree not to:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Falsify clock-in or clock-out events, geolocation data, or task completions;</li>
            <li>Attempt to bypass the geofence (e.g. spoof your device location);</li>
            <li>Upload photos that depict anything other than the relevant cleaning task;</li>
            <li>Interfere with, probe, or attempt to gain unauthorised access to the Service;</li>
            <li>Use the Service for any unlawful purpose or to harass, abuse or harm any person.</li>
          </ul>
        </Section>

        <Section title="4. Workforce Data & Compliance">
          The Service is a workforce-management tool. We record sign-in and
          sign-out events, working hours, geolocation samples while a shift is
          active, cleaning-task completions, and any photos you upload as
          evidence of completed tasks. This information is used by the Company
          to roster staff, pay wages, audit attendance and meet client SLAs.
          By using the Service you authorise the Company to collect and use
          this information for those purposes.
        </Section>

        <Section title="5. Geolocation Tracking">
          The Service captures your device location only:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>At the moment you press <em>Clock in</em> or <em>Clock out</em>;</li>
            <li>Periodically while you are clocked-in, to confirm you remained on the worksite.</li>
          </ul>
          You can withdraw geolocation permission at any time via your device
          settings, but doing so will prevent the Service from accepting your
          clock-in. See our Privacy Policy for retention details.
        </Section>

        <Section title="6. Photo Uploads">
          Photos you upload via the cleaning checklist become part of the
          Company&rsquo;s compliance records. Do not include people&rsquo;s faces,
          identifying personal items, or any client-confidential material in
          the frame. The Company may share photos with the relevant client as
          proof of work.
        </Section>

        <Section title="7. Suspension & Termination">
          The Company may suspend or terminate your access at any time if you
          breach these Terms, if your employment or contract with the Company
          ends, or if continued access would expose the Company to legal or
          regulatory risk. On termination, your historical attendance and
          compliance records will be retained for as long as required by law.
        </Section>

        <Section title="8. Intellectual Property">
          The Service, including its source code, design, logos and copy, is
          owned by the Company and protected by Australian and international
          copyright and trademark law. You may use the Service only as
          authorised by these Terms.
        </Section>

        <Section title="9. Disclaimers">
          The Service is provided &ldquo;as is&rdquo; without warranties of any kind. The
          Company does not warrant that the Service will be uninterrupted,
          accurate, free from errors, or that any location reading will be
          precise. To the extent permitted by law the Company disclaims all
          implied warranties.
        </Section>

        <Section title="10. Limitation of Liability">
          To the maximum extent permitted by law, neither the Company nor any
          of its officers, employees or contractors will be liable to you for
          any indirect, incidental, special or consequential damages arising
          out of or in connection with your use of the Service. Nothing in
          these Terms limits any consumer guarantees that cannot be excluded
          under the Australian Consumer Law.
        </Section>

        <Section title="11. Governing Law">
          These Terms are governed by {LEGAL.governingLaw}. You and the
          Company submit to the exclusive jurisdiction of the courts of
          {" "}{LEGAL.jurisdiction} in respect of any dispute arising under
          these Terms.
        </Section>

        <Section title="12. Changes">
          We may amend these Terms from time to time. Material changes will
          be notified by an in-app banner the next time you sign in.
          Continued use of the Service after a notice constitutes acceptance
          of the updated Terms.
        </Section>

        <Section title="13. Contact">
          Questions about these Terms? Write to{" "}
          <a className="text-[#002FA7] hover:underline" href={`mailto:${LEGAL.contactEmail}`}>
            {LEGAL.contactEmail}
          </a>{" "}or call {LEGAL.supportPhone}.
        </Section>
      </main>

      <LegalFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-black text-lg tracking-tight mb-2">{title}</h2>
      <div className="text-sm text-gray-800 leading-relaxed">{children}</div>
    </section>
  );
}
